import {
  Box,
  Link,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { formatNumber } from '../../utils/Common'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

const StyledTableCell = styled(TableCell)(() => ({
  border: 0,
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#E5F0FF',
    color: '#06004A',

    fontSize: '10px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 'normal',
    textTransform: 'uppercase',
  },
  [`&.${tableCellClasses.body}`]: {
    color: '#06004A',

    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
  },
}))

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#F8F8F8',
  },
  border: 0,
}))

const SvgIcon = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='7'
      height='10'
      viewBox='0 0 7 10'
      fill='none'
    >
      <path
        d='M6.45898 3.83887L3.50142 0.881306L0.543863 3.83887'
        stroke='#06004A'
        strokeMiterlimit='10'
      />
      <path
        d='M6.45898 6.04199L3.50142 8.99955L0.543863 6.04199'
        stroke='#06004A'
        strokeMiterlimit='10'
      />
    </svg>
  )
}

const ItemList = () => {
  const getParentGroupColor = (name: string) => {
    switch (name) {
      case 'Electrical':
        return '#FF653F'
      case 'Plumbing':
        return '#3F69FF'
      case 'Mech':
        return '#21E4BD'
      case 'Hardwood / Stone':
        return '#2EB1CE'
      case 'Cladding':
        return '#2CE267'
      case 'Windows / Doors':
        return '#DD9E23'
      case 'Exterior Other':
        return '#E5F0FF'
      case 'Building Supply':
        return '#CDF765'
      case 'Cabinets / Vanities':
        return '#6585F7'
      case 'Applainces':
        return '#4DFE86'
      case 'Hardscaping':
        return '#2D248B'
      case 'Other':
      default:
        return '#A7C6F1'
    }
  }

  const itemList = useSelector((state: RootState) => state.takeOff.itemList)

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#FFFFFF',
        pointerEvents: 'auto',
        padding: '6px 6px 0 6px',
        borderRadius: '4px',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&-ms-overflow-style:': {
          display: 'none',
        },
      }}
    >
      <TableContainer sx={{ borderRadius: '2px 2px 0px 0px' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  Unit Name
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  Qty
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  Parent Group
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  Brand
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  Model Link
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  History
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  Unit Cost
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '6px',
                    alignItems: 'center',
                  }}
                >
                  Total Cost
                  {<SvgIcon />}
                </Box>
              </StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {itemList.map((item, index) => (
              <StyledTableRow key={index}>
                <StyledTableCell
                  sx={{
                    borderRight: '1px solid #E4E4E4',
                  }}
                >
                  {item.unitName}
                </StyledTableCell>
                <StyledTableCell>{item.quantity}</StyledTableCell>
                <StyledTableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='8'
                      height='8'
                      viewBox='0 0 8 8'
                      fill='none'
                    >
                      <circle
                        cx='4'
                        cy='4'
                        r='4'
                        fill={getParentGroupColor(item.parentGroup)}
                      />
                    </svg>

                    {item.parentGroup}
                  </Box>
                </StyledTableCell>
                <StyledTableCell>{item.brand}</StyledTableCell>
                <StyledTableCell>
                  <Link
                    href={item.modelLink}
                    sx={{
                      color: '#1705E5',

                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'solid',
                      textDecorationSkipInk: 'auto',
                      textDecorationThickness: 'auto',
                      textUnderlineOffset: 'auto',
                      textUnderlinePosition: 'from-font',
                    }}
                  >
                    {item.modelLinkName}
                  </Link>
                </StyledTableCell>
                <StyledTableCell>
                  {
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {[
                        '/images/profile1.png',
                        '/images/profile2.png',
                        '/images/profile3.png',
                      ].map((src, index) => (
                        <img
                          key={index}
                          src={src}
                          alt={`dropdown ${index}`}
                          style={{ display: 'block', marginRight: '2px' }}
                        />
                      ))}
                    </Box>
                  }
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    borderLeft: '1px solid #E4E4E4',
                  }}
                >
                  ${formatNumber(item.unitCost)}
                </StyledTableCell>
                <StyledTableCell
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#F7FAFF' : '#DAE6F7',
                  }}
                >
                  ${formatNumber(item.totalCost)}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default ItemList
